import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { Tooltip as RechartsTooltip } from 'recharts';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { adminApi } from '../services/api';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';
import { UserEngagementAnalytics } from '../types';

interface UserEngagementAnalyticsProps {
  isVisible?: boolean;
}

const UserEngagementAnalyticsComponent: React.FC<UserEngagementAnalyticsProps> = ({ isVisible = true }) => {
  const [data, setData] = useState<UserEngagementAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminApi.getUserEngagementAnalytics();
      setData(result);
    } catch (err: any) {
      setError('Kullanıcı etkileşim analitikleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Use visibility refresh hook
  const { lastRefresh, manualRefresh } = useVisibilityRefresh(fetchData, {
    refreshInterval: 60000, // 60 seconds
    isVisible,
    enabled: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ height: 420, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress size={40} thickness={4} />
        </Box>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ height: 420, borderRadius: 2, border: '1px solid', borderColor: 'grey.200', p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error || 'Veri yüklenemedi'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with refresh info */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={3}
        sx={{ px: { xs: 1, sm: 2 } }}
      >
        <Typography variant="h4" sx={{ 
          fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' },
          fontWeight: 600,
          color: 'text.primary'
        }}>
          Kullanıcı Etkileşim Analitikleri
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            {format(lastRefresh, 'HH:mm')}
          </Typography>
          <Tooltip title="Yenile">
            <IconButton 
              onClick={manualRefresh} 
              disabled={loading}
              sx={{ 
                p: 0.5,
                borderRadius: 1,
                backgroundColor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
                '&:hover': {
                  backgroundColor: 'grey.100'
                }
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary Stats - Single Row */}
      <Box sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.200',
        backgroundColor: 'white',
        p: 2.5,
        mb: 3
      }}>
        <Box 
          display="grid" 
          gridTemplateColumns={{ 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(3, 1fr)'
          }} 
          gap={3}
        >
          {/* Active Users */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
              {data.activeUsers}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              Aktif Kullanıcılar
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              Son 30 günde aktif
            </Typography>
          </Box>

          {/* Users with Favorites */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main', mb: 0.5 }}>
              {data.usersWithFavorites}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              Favori Kullanan Kullanıcılar
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              Favori hayvanları olan
            </Typography>
          </Box>

          {/* Email Verification Rate */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main', mb: 0.5 }}>
              %{data.verificationRate}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              E-posta Doğrulama Oranı
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              Doğrulaması yapılan kullanıcılar
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box 
        display="grid" 
        gridTemplateColumns={{ 
          xs: '1fr', 
          sm: '1fr', 
          md: '1fr'
        }} 
        gap={{ xs: 2, sm: 2.5, md: 3 }}
      >

        {/* Users by City */}
        <Box sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: 'white',
          p: 2.5,
          gridColumn: { xs: '1', md: '1' },
          minHeight: 380
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            Şehir Dağılımı
          </Typography>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.usersByCity.slice(0, 10)}>
              <CartesianGrid strokeDasharray="1 1" stroke="#f0f0f0" />
              <XAxis 
                dataKey="_id" 
                angle={-45}
                textAnchor="end"
                height={80}
                fontSize={11}
                stroke="#666"
              />
              <YAxis fontSize={11} stroke="#666" />
              <RechartsTooltip 
                contentStyle={{
                  borderRadius: 8,
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
              <Bar 
                dataKey="count" 
                fill="#667eea" 
                radius={[2, 2, 0, 0]}
                stroke="#667eea"
                strokeWidth={0.5}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default UserEngagementAnalyticsComponent;
